import { useFormContext } from "react-hook-form";

import { useThemePreviewContext } from "containers/admin/clients/client/theme-page/contexts/theme-preview-context";

import { PaddingPropertyType } from "containers/admin/clients/client/theme-page/preview/sub-tabs/layout/layout.types";
import {
  AppButtonFormData,
  AppLayoutFormData,
  LayoutPaddingBlocks,
  LayoutResponsivePadding,
} from "containers/admin/clients/client/theme-page/preview/preview.types";
import {
  MOBILE_VALUE,
  DESKTOP_VALUE,
  LEFT_VALUE,
  TOP_VALUE,
  RIGHT_VALUE,
  BOTTOM_VALUE,
} from "containers/admin/clients/client/theme-page/preview/sub-tabs/layout/layout.constants";

export const useThemePadding = (formFieldPrefix: string) => {
  const { setButton, setLayout } = useThemePreviewContext();
  const { getValues, setValue } = useFormContext();

  // Update context based on section and associated nesting level
  const handleContextUpdate = (direction: PaddingPropertyType, value: string) => {
    // Extract section name from formFieldPrefix
    const sectionName = formFieldPrefix.split(".")[0];

    if (sectionName === "button") {
      // Case 1: Button padding
      setButton((prev: AppButtonFormData | null) => {
        if (!prev) return prev;
        return {
          ...prev,
          padding: { ...prev.padding, [direction]: value },
        };
      });
    } else if (sectionName === "layout") {
      // Case 2: Layout padding - handle different nesting levels
      const pathParts = formFieldPrefix.split(".");

      if (pathParts.length === 4) {
        // ex) layout.contentBlock.heading.mobile -> 4 levels
        const [, contentBlock, heading, device] = pathParts;
        setLayout((prev: AppLayoutFormData | null) => {
          if (!prev) return prev;
          return {
            ...prev,
            contentBlock: {
              ...prev.contentBlock,
              [heading]: {
                ...prev.contentBlock[heading as keyof LayoutPaddingBlocks],
                [device]: {
                  ...prev.contentBlock[heading as keyof LayoutPaddingBlocks][
                    device as keyof LayoutResponsivePadding
                  ],
                  [direction]: value,
                },
              },
            },
          };
        });
      } else if (pathParts.length === 2 && pathParts[1] === "contentArea") {
        // ex) layout.contentArea -> 2 levels (no mobile/desktop nesting)
        setLayout((prev: AppLayoutFormData | null) => {
          if (!prev) return prev;
          return {
            ...prev,
            contentArea: { ...prev.contentArea, [direction]: value },
          };
        });
      }
    }
  };

  // Handle change for simple padding
  const handleSimplePaddingChange = (direction: "x" | "y", value: string) => {
    const cleanedValue = cleanPaddingValue(value);

    if (direction === "x") {
      setValue(`${formFieldPrefix}.${LEFT_VALUE}`, cleanedValue, { shouldDirty: true });
      handleContextUpdate(LEFT_VALUE, cleanedValue);

      setValue(`${formFieldPrefix}.${RIGHT_VALUE}`, cleanedValue, { shouldDirty: true });
      handleContextUpdate(RIGHT_VALUE, cleanedValue);
    } else {
      setValue(`${formFieldPrefix}.${TOP_VALUE}`, cleanedValue, { shouldDirty: true });
      handleContextUpdate(TOP_VALUE, cleanedValue);

      setValue(`${formFieldPrefix}.${BOTTOM_VALUE}`, cleanedValue, { shouldDirty: true });
      handleContextUpdate(BOTTOM_VALUE, cleanedValue);
    }
  };

  // Handler for custom padding toggle button
  const handleCustomModeToggle = (currentIsCustom: boolean, onChange: (value: boolean) => void) => {
    const newIsCustomMode = !currentIsCustom;
    onChange(newIsCustomMode);

    // handle different nesting levels
    if (formFieldPrefix === "layout.contentArea") {
      setLayout((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          contentArea: {
            ...prev.contentArea,
            isCustom: newIsCustomMode,
          },
          contentBlock: prev.contentBlock,
        };
      });
    } else if (formFieldPrefix.includes("layout.contentBlock")) {
      const pathParts = formFieldPrefix.split(".");
      const blockType = pathParts[2];
      const isMobile = pathParts[3] === MOBILE_VALUE;

      if (blockType && blockType !== "group") {
        setLayout((prev) => {
          if (!prev) return prev;
          const blockData = prev.contentBlock[blockType as keyof LayoutPaddingBlocks];
          return {
            ...prev,
            contentBlock: {
              ...prev.contentBlock,
              [blockType]: {
                ...blockData,
                [isMobile ? MOBILE_VALUE : DESKTOP_VALUE]: {
                  ...blockData[isMobile ? MOBILE_VALUE : DESKTOP_VALUE],
                  isCustom: newIsCustomMode,
                },
              },
            },
          };
        });
      }
    }

    // If we're turning off custom mode, check if values need to be reset
    if (!newIsCustomMode) {
      const leftValue = getValues(`${formFieldPrefix}.${LEFT_VALUE}`);
      const rightValue = getValues(`${formFieldPrefix}.${RIGHT_VALUE}`);
      const topValue = getValues(`${formFieldPrefix}.${TOP_VALUE}`);
      const bottomValue = getValues(`${formFieldPrefix}.${BOTTOM_VALUE}`);

      // Only update if values are different
      if (leftValue !== rightValue) {
        handleSimplePaddingChange("x", leftValue);
      }
      if (topValue !== bottomValue) {
        handleSimplePaddingChange("y", topValue);
      }
    }
  };

  // Helper function to clean up padding values
  const cleanPaddingValue = (value: string): string => {
    let cleanedValue = value;

    // remove leading zeros
    cleanedValue = cleanedValue.replace(/^0+(?=\d)/, "");

    // limit to 2 digits
    if (cleanedValue.length > 2) {
      cleanedValue = cleanedValue.slice(0, 2);
    }

    return cleanedValue;
  };

  // Handle blur for padding inputs
  const handleCustomPaddingBlur = (
    value: string,
    direction: PaddingPropertyType,
    fieldOnChange: (value: string) => void,
  ) => {
    // reset to 0 if empty
    if (value === "") {
      handleCustomPaddingChange(direction, "0", fieldOnChange);
    }
    if (Number(value) > 60) {
      handleCustomPaddingChange(direction, "60", fieldOnChange);
    }
  };

  // Handle custom padding change for individual directions
  const handleCustomPaddingChange = (
    direction: PaddingPropertyType,
    value: string,
    fieldOnChange: (value: string) => void,
  ) => {
    const cleanedValue = cleanPaddingValue(value);

    // Always update form state if field is provided
    fieldOnChange(cleanedValue);

    // Only update context if value is not empty
    if (cleanedValue !== "" && Number(cleanedValue) <= 60) {
      handleContextUpdate(direction, cleanedValue);
    }
  };

  return {
    handleSimplePaddingChange,
    handleCustomModeToggle,
    handleCustomPaddingBlur,
    handleCustomPaddingChange,
  };
};
